import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { StepHeader } from "@/components/activation/StepHeader"
import { AskRobPanel } from "@/components/activation/AskRobPanel"
import { StepNavigation } from "@/components/activation/StepNavigation"
import { SelectableCard, CheckIndicator } from "@/components/activation/SelectableCard"
import { INVESTMENT_STRATEGIES } from "@/components/activation/data/strategies"
import { decimalOnly, INPUT_PATTERNS } from "@/components/activation/input-sanitizers"
import { useAskRobChat } from "@/hooks/use-ask-rob-chat"
import { useActivation } from "@/lib/activation-context"
import { cn } from "@/lib/utils"

const METRIC_OPTIONS = [
  { id: "roce", title: "Return on Capital Employed (%)", helper: "ROCE as percentage return", placeholder: "e.g. 15", unit: "%" },
  { id: "roc", title: "Profit on Cost (%)", helper: "Profit as percentage of total cost", placeholder: "e.g. 20", unit: "%" },
  { id: "profit", title: "Profit on GDV (%)", helper: "Profit as percentage of GDV", placeholder: "e.g. 20", unit: "%" },
  { id: "cashflow", title: "Minimum Monthly Cashflow (£)", helper: "Monthly cashflow in pounds", placeholder: "e.g. 200", unit: "£" },
]

const THRESHOLD_LABELS = {
  roce: "Minimum ROCE (%)",
  roc: "Minimum Profit on Cost (%)",
  profit: "Minimum Profit on GDV (%)",
  cashflow: "Minimum Monthly Cashflow (£)",
}

const PROMPTS = ["Help me define my deal criteria"]

const SECONDARY_CRITERIA = {
  passive: { metricIds: ["cashflow"], required: false },
  active: { metricIds: ["cashflow"], required: false },
  operational: { metricIds: ["cashflow"], required: false },
  "heavy-execution": { metricIds: ["roc", "profit"], required: true },
}

const DEFAULT_SECONDARY_CRITERIA = {
  metricIds: METRIC_OPTIONS.map((metric) => metric.id),
  required: false,
}

function secondaryCriteriaFor(operatingPreference) {
  return SECONDARY_CRITERIA[operatingPreference] ?? DEFAULT_SECONDARY_CRITERIA
}

export function CriteriaStep() {
  const { step1, step2, step3, step4, step5, saveStep, askStepFiveAgent, serverError } = useActivation()
  const navigate = useNavigate()
  const secondaryCriteria = secondaryCriteriaFor(step2.operatingPreference)
  const initialSecondaryMetric = secondaryCriteria.metricIds.includes(step5.secondaryMetric)
    ? step5.secondaryMetric
    : ""

  const [primaryMetric, setPrimaryMetric] = useState(step5.primaryMetric)
  const [primaryThreshold, setPrimaryThreshold] = useState(step5.primaryThreshold)
  const [secondaryMetric, setSecondaryMetric] = useState(initialSecondaryMetric)
  const [secondaryThreshold, setSecondaryThreshold] = useState(
    initialSecondaryMetric ? step5.secondaryThreshold : "",
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const askRobChat = useAskRobChat()

  const primaryMetricDef = METRIC_OPTIONS.find((m) => m.id === primaryMetric)
  const primaryStrategyTitle = INVESTMENT_STRATEGIES.find((s) => s.id === step2.primaryStrategy)?.title
  const secondaryMetricOptions = METRIC_OPTIONS.filter(
    (metric) => secondaryCriteria.metricIds.includes(metric.id) && metric.id !== primaryMetric,
  )
  const secondaryFilterValid = secondaryCriteria.required
    ? Boolean(secondaryMetric) && Boolean(secondaryThreshold)
    : !secondaryMetric || Boolean(secondaryThreshold)

  const canContinue =
    Boolean(primaryMetric) &&
    Boolean(primaryThreshold) &&
    secondaryFilterValid

  const onSubmit = async (e) => {
    e.preventDefault()
    if (!canContinue) return
    setIsSubmitting(true)
    setSubmitError(null)
    try {
      await saveStep(5, { primaryMetric, primaryThreshold, secondaryMetric, secondaryThreshold })
      navigate("/dashboard")
    } catch (error) {
      setSubmitError(error.message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const applySuggestion = (messageIndex, suggestionId) => {
    const suggestion = askRobChat.findSuggestion(messageIndex, suggestionId)
    if (!suggestion || suggestion.status === "applied" || suggestion.status === "dismissed") return

    if (suggestion.field === "primaryMetric") {
      setPrimaryMetric(suggestion.suggestedValue ?? "")
      setPrimaryThreshold("")
      askRobChat.updateSuggestionStatus(messageIndex, suggestionId, "applied")
      return
    }

    if (suggestion.field === "primaryThreshold") {
      setPrimaryThreshold(suggestion.suggestedValue ?? "")
      askRobChat.updateSuggestionStatus(messageIndex, suggestionId, "applied")
      return
    }

    if (suggestion.field === "secondaryMetric") {
      const suggestedMetric = suggestion.suggestedValue ?? ""
      const allowed = !suggestedMetric || secondaryCriteria.metricIds.includes(suggestedMetric)
      if (!allowed || (secondaryCriteria.required && !suggestedMetric)) return

      setSecondaryMetric(suggestedMetric)
      setSecondaryThreshold("")
      askRobChat.updateSuggestionStatus(messageIndex, suggestionId, "applied")
      return
    }

    if (suggestion.field === "secondaryThreshold") {
      setSecondaryThreshold(suggestion.suggestedValue ?? "")
      askRobChat.updateSuggestionStatus(messageIndex, suggestionId, "applied")
    }
  }

  const dismissSuggestion = askRobChat.dismissSuggestion

  const askRob = async (message) => {
    await askRobChat.submitMessage(message, ({ message: userMessage, conversation }) => (
      askStepFiveAgent({
        message: userMessage,
        conversation,
        currentValues: { primaryMetric, primaryThreshold, secondaryMetric, secondaryThreshold },
        stepOneValues: step1,
        stepTwoValues: step2,
        stepThreeValues: step3,
        stepFourValues: step4,
      })
    ))
  }

  return (
    <form onSubmit={onSubmit} noValidate className="animate-in fade-in slide-in-from-bottom-4 duration-300">
      <div className="mx-auto w-full max-w-7xl px-6 py-12 sm:px-8 lg:px-12">
        <StepHeader
          step={5}
          title="Define Deal Criteria"
          description="Set the minimum thresholds a deal must meet before you pursue or offer."
        />

        <div className="grid grid-cols-1 gap-12 lg:grid-cols-5">
          <div className="space-y-8 lg:col-span-3">
            <div className="flex flex-wrap gap-2">
              <div className="rounded-lg border border-border bg-muted px-3 py-2">
                <span className="text-xs font-semibold text-foreground">
                  {step3.selectedPostcodes.length ? step3.selectedPostcodes.join(", ") : "No area selected"}
                </span>
              </div>
              <div className="rounded-lg border border-border bg-muted px-3 py-2">
                <span className="text-xs font-semibold text-foreground">
                  {primaryStrategyTitle || "No strategy"}
                </span>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <h2 className="mb-1 text-sm font-semibold text-foreground">
                  What defines a good deal? <span className="text-destructive">*</span>
                </h2>
                <p className="text-xs text-muted-foreground">Select ONE primary metric to filter opportunities</p>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {METRIC_OPTIONS.map((m) => {
                  const selected = primaryMetric === m.id
                  return (
                    <SelectableCard
                      key={m.id}
                      selected={selected}
                      onClick={() => setPrimaryMetric(m.id)}
                      bg="bg-card"
                    >
                      <div className="mb-2 flex items-center justify-between">
                        <h3 className="text-sm font-bold text-foreground">{m.title}</h3>
                        <CheckIndicator selected={selected} />
                      </div>
                      <p className="text-xs text-muted-foreground">{m.helper}</p>
                    </SelectableCard>
                  )
                })}
              </div>
            </div>

            {primaryMetric ? (
              <div className="space-y-4">
                <div>
                  <h2 className="mb-1 text-sm font-semibold text-foreground">
                    Set Your Minimum Threshold <span className="text-destructive">*</span>
                  </h2>
                  <p className="text-xs text-muted-foreground">Define the minimum value required for this metric</p>
                </div>

                <div className="space-y-2">
                  <label htmlFor="primary-threshold" className="text-sm font-semibold text-foreground">
                    {THRESHOLD_LABELS[primaryMetric]}
                  </label>
                  <ThresholdInput
                    id="primary-threshold"
                    metric={primaryMetric}
                    value={primaryThreshold}
                    onChange={setPrimaryThreshold}
                    placeholder={primaryMetricDef?.placeholder}
                  />
                  <p className="text-xs text-muted-foreground">Deals below this threshold will not qualify for pursuit</p>
                </div>
              </div>
            ) : null}

            <div className="space-y-4">
              <div>
                <h2 className="mb-1 text-sm font-semibold text-foreground">
                  {secondaryCriteria.required ? (
                    <>Add Development Filter <span className="text-destructive">*</span></>
                  ) : "Add Secondary Filter (Optional)"}
                </h2>
                <p className="text-xs text-muted-foreground">
                  {secondaryCriteria.required
                    ? "Select the development return metric this deal must achieve"
                    : "Apply an additional constraint to refine deal selection"}
                </p>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <select
                  value={secondaryMetric}
                  onChange={(e) => { setSecondaryMetric(e.target.value); setSecondaryThreshold("") }}
                  aria-label={secondaryCriteria.required ? "Development metric" : "Secondary metric"}
                  className="rounded-lg border border-border bg-background px-4 py-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <option value="">
                    {secondaryCriteria.required ? "Select development metric..." : "Select metric..."}
                  </option>
                  {secondaryMetricOptions.map((metric) => (
                    <option key={metric.id} value={metric.id}>{metric.title}</option>
                  ))}
                </select>

                {secondaryMetric ? (
                  <ThresholdInput
                    metric={secondaryMetric}
                    value={secondaryThreshold}
                    onChange={setSecondaryThreshold}
                    placeholder="Threshold..."
                    aria-label="Secondary threshold"
                  />
                ) : null}
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <AskRobPanel
              subtitle="Get guidance on setting realistic deal criteria"
              prompts={PROMPTS}
              messages={askRobChat.messages}
              isLoading={askRobChat.isLoading}
              error={askRobChat.error}
              suggestionMode="sequential"
              onAsk={askRob}
              onApplySuggestion={applySuggestion}
              onDismissSuggestion={dismissSuggestion}
            />
          </div>
        </div>

        <StepNavigation
          stepNumber={5}
          canContinue={canContinue}
          isSubmitting={isSubmitting}
          continueLabel="Lock Deal Criteria"
          onBack={() => navigate("/activation/step/4")}
        />
        {submitError || serverError ? (
          <p className="mt-4 text-center text-sm text-destructive">{submitError || serverError}</p>
        ) : null}
      </div>
    </form>
  )
}

function ThresholdInput({ metric, value, onChange, placeholder, id, ...rest }) {
  const isCashflow = metric === "cashflow"
  return (
    <div className="relative">
      {isCashflow ? (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 font-medium text-muted-foreground">£</span>
      ) : null}
      <input
        id={id}
        type="text"
        inputMode="decimal"
        pattern={INPUT_PATTERNS.decimal}
        autoComplete="off"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(decimalOnly(e.target.value))}
        className={cn(
          "w-full rounded-lg border border-border bg-background py-3 pr-4 text-sm transition-all focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20",
          isCashflow ? "pl-8" : "pl-4",
        )}
        {...rest}
      />
      {!isCashflow ? (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 font-medium text-muted-foreground">%</span>
      ) : null}
    </div>
  )
}
